import { apiClient } from '@/api/client';
import axios from 'axios';

// ── settings + module toggle ──────────────────────────────────────
export const getMeetingSettings = () =>
  apiClient.get('/morning-meeting/settings').then((r) => r.data);

export const setMeetingEnabled = (enabled) =>
  apiClient.put('/morning-meeting/settings', { enabled }).then((r) => r.data);

// ── meeting CRUD ──────────────────────────────────────────────────
export const listMeetingDefs = () =>
  apiClient.get('/morning-meeting/meetings').then((r) => r.data);

export const createMeetingDef = (patch) =>
  apiClient.post('/morning-meeting/meetings', patch).then((r) => r.data);

export const updateMeetingDef = (id, patch) =>
  apiClient.put(`/morning-meeting/meetings/${id}`, patch).then((r) => r.data);

export const deleteMeetingDef = (id) =>
  apiClient.delete(`/morning-meeting/meetings/${id}`).then((r) => r.data);

export const restoreMeetingDef = (id) =>
  apiClient.post(`/morning-meeting/meetings/${id}/restore`).then((r) => r.data);

export const getMeetingMembers = () =>
  apiClient.get('/morning-meeting/members').then((r) => r.data);

// ── occurrences + live ────────────────────────────────────────────
export const getMeetingOccurrences = ({ pollTag } = {}) =>
  apiClient.get('/morning-meeting/occurrences', {
    ...(pollTag ? { headers: { 'X-Trigger': `poll:${pollTag}` } } : {}),
  }).then((r) => r.data);

export const getMeetingNext = () =>
  apiClient.get('/morning-meeting/next').then((r) => r.data);

export const joinMeeting = (meetingId) =>
  apiClient.post('/morning-meeting/join', { meeting_id: meetingId || null }).then((r) => r.data);

export const startInstantMeeting = (meetingId) =>
  apiClient.post('/morning-meeting/start-now', { meeting_id: meetingId || null }).then((r) => r.data);

export const endMeetingSession = (sessionId) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/end`).then((r) => r.data);

export const setMeetingHost = (sessionId, userId) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/host`, { user_id: userId }).then((r) => r.data);

export const claimMeetingHost = (sessionId) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/host/claim`).then((r) => r.data);

export const releaseMeetingHost = (sessionId) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/host/release`).then((r) => r.data);

export const listMeetingSessions = ({ saved } = {}) =>
  apiClient.get('/morning-meeting/sessions', saved ? { params: { saved: true } } : undefined).then((r) => r.data);

export const setSessionRecordingSaved = (sessionId, saved) =>
  apiClient.put(`/morning-meeting/sessions/${sessionId}/recording/saved`, { saved }).then((r) => r.data);

export const getSessionRecording = (sessionId) =>
  apiClient.get(`/morning-meeting/sessions/${sessionId}/recording`).then((r) => r.data);

export const deleteSessionRecording = (sessionId) =>
  apiClient.delete(`/morning-meeting/sessions/${sessionId}/recording`).then((r) => r.data);

// ── instant (ad-hoc) meetings + shareable links ───────────────────
export const startAdhocMeeting = (opts) =>
  apiClient.post('/morning-meeting/instant', opts || {}).then((r) => r.data);

export const joinMeetingSession = (sessionId) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/join`).then((r) => r.data);

export const getSessionShare = (sessionId) =>
  apiClient.get(`/morning-meeting/sessions/${sessionId}/share`).then((r) => r.data);

export const setSessionGuestLink = (sessionId, enabled) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/guest-link`, { enabled }).then((r) => r.data);

// Guest endpoints are PUBLIC (no auth) — use a bare client with no Bearer/refresh
// interceptors so an unauthenticated guest never triggers a token refresh.
const guestClient = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL || ''}/api` });

export const getGuestMeetingInfo = (token) =>
  guestClient.get(`/morning-meeting/guest/${token}`).then((r) => r.data);

// Lobby: guests request to join, then poll until a host admits (or denies).
export const requestGuestJoin = (token, name, email) =>
  guestClient.post(`/morning-meeting/guest/${token}/request`, { name, email }).then((r) => r.data);

export const getGuestRequestStatus = (token, requestId) =>
  guestClient.get(`/morning-meeting/guest/${token}/request/${requestId}`).then((r) => r.data);

// Host/manager side of the lobby.
export const listGuestRequests = (sessionId, { pollTag } = {}) =>
  apiClient.get(`/morning-meeting/sessions/${sessionId}/guest-requests`, {
    ...(pollTag ? { headers: { 'X-Trigger': `poll:${pollTag}` } } : {}),
  }).then((r) => r.data);

export const decideGuestRequest = (sessionId, requestId, action) =>
  apiClient.post(`/morning-meeting/sessions/${sessionId}/guest-requests/${requestId}`, { action }).then((r) => r.data);

// ── background pictures (own + company) ──────────────────────────
export const listBackgrounds = () =>
  apiClient.get('/morning-meeting/backgrounds').then((r) => r.data);

export const uploadBackground = (file, scope = 'mine') => {
  const fd = new FormData();
  fd.append('file', file);
  return apiClient.post('/morning-meeting/backgrounds', fd, { params: { scope } }).then((r) => r.data);
};

export const deleteBackground = (id) =>
  apiClient.delete(`/morning-meeting/backgrounds/${id}`).then((r) => r.data);

export const backgroundUrl = (id, thumb = false) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/morning-meeting/backgrounds/${id}/file${thumb ? '?size=thumb' : ''}`;

export const fetchBackgroundBytes = (id) =>
  apiClient.get(`/morning-meeting/backgrounds/${id}/file`, { responseType: 'arraybuffer' }).then((r) => r.data);
